import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { CaseActionBuilderName } from 'dashboard/models/case-action';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TriggerCaseParamSchema } from 'dashboard/models/trigger';
import { Workflow } from 'dashboard/models/workflow';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setFilters } from 'dashboard/redux/actions/workflows';
import { RootState } from 'dashboard/redux/store';
import { loadCaseTemplatesList } from 'dashboard/redux/thunk-actions/case-templates';
import { createWorkflow } from 'dashboard/redux/thunk-actions/workflow';
import { loadWorkflows } from 'dashboard/redux/thunk-actions/workflows';
import DashboardCaseActionsView from 'dashboard/views/DashboardCaseActionsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { generateWorkflowToken } from 'lib/workflows';

const DashboardCaseActionsRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesDashboard,
    props.currentEnvironment.type,
  );

  const { loading: loadingCaseActions, callback: caseActionsLoader } = useApiLoader({
    loader: async () => {
      await props.loadWorkflows({
        triggerTypes: [ApiModel.TriggerCase],
        omni: props.filters.omni,
      });
    },
    deps: [props.filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case actions.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: async () => {
      const caseTemplateIds = props.actionWorkflows.flatMap((wf) =>
        wf.relationships['case-templates'].data?.map((ct) => ct.id),
      );
      props.loadCaseTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: { caseTemplateIds },
      });
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflow data.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadCaseTemplatesList, props.actionWorkflows],
  });

  const { error: archiveCaseActionWorkflowError, callback: archiveCaseActionWorkflow } =
    useApiCallback(
      async (actionWorkflow: Workflow) => {
        await SessionClientDashboard.deleteResource<Workflow>(
          ApiEndpoint.Workflow,
          actionWorkflow.id,
        );
        caseActionsLoader();
        props.showMessage('Successfully archived case action.', MessageType.Success);
      },
      [caseActionsLoader],
    );

  useApiErrorHandler({
    error: archiveCaseActionWorkflowError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to archive case action, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to archive case action, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { handleOmniSearch } = useTable({
    filters: props.filters,
    setFilters: props.setFilters,
  });

  const handleCreateCaseAction = async (builder: CaseActionBuilderName) => {
    const { redirectPath } = await props.createWorkflow(
      props.currentEnvironment.id,
      `Custom Action #${generateWorkflowToken().slice(0, 4)}`, //pre-defined case action name
      {
        type: ApiModel.TriggerCase,
        extra: {
          'param-schema': [] as TriggerCaseParamSchema[],
        },
      },
      '',
      builder,
    );

    navigate(redirectPath);
  };

  return (
    <DashboardCaseActionsView
      isLoading={loadingCaseActions}
      productFeatureIsDisabled={productFeatureIsDisabled}
      actionWorkflows={props.actionWorkflows}
      caseTemplates={props.caseTemplates}
      workflowDeployments={props.workflowDeployments}
      filters={props.filters}
      handleOmniSearch={handleOmniSearch}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleCreateCaseAction={handleCreateCaseAction}
      archiveActionWorkflow={archiveCaseActionWorkflow}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    actionWorkflows: state.workflows.workflows,
    workflowDeployments: state.workflows.workflowDeployments,
    caseTemplates: state.caseTemplates.caseTemplates,
    filters: state.workflows.filters,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  loadWorkflows,
  loadCaseTemplatesList,
  createWorkflow,
  setFilters,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseActionsRoute);
